import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import apiClient from "../apiClient"; // adjust path to wherever this lives in your project
import { fetchUserSubscriptions } from "../apiService"; // adjust path to wherever this lives in your project
import FeedbackLoginModal from "../Components/Feedbackloginmodal"; // adjust path to wherever this lives in your project

const categories = [
  { id: "speed", label: "Connection Speed", icon: "⚡" },
  { id: "reliability", label: "Reliability", icon: "🔒" },
  { id: "support", label: "Customer Support", icon: "🎧" },
  { id: "value", label: "Value for Money", icon: "💰" },
  { id: "installation", label: "Installation Experience", icon: "🔧" },
];

const issueOptions = [
  "Slow speeds during peak hours",
  "Frequent disconnections",
  "Poor customer support response",
  "Installation delays",
  "Billing issues",
  "Router/equipment problems",
  "No issues experienced",
];

// Decode a JWT's payload without pulling in a library and check its `exp`
// claim. This is what lets us catch a *stale* token in localStorage (one
// that exists but has already expired) and re-show the login modal instead
// of silently proceeding as if the user were still authenticated.
//
// Returns true when the token looks usable. If we can't decode it, or it
// has no `exp` claim, we don't block on our own guess — an actual 401 from
// the API is still the fallback signal (see handleSubmit / loadAccountInfo).
function isTokenValid(token: string | null): boolean {
  if (!token) return false;
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    if (!payload?.exp) return true;
    return payload.exp * 1000 > Date.now();
  } catch {
    return true;
  }
}

export default function Feedback() {
  const navigate = useNavigate();

  // Auth gate — feedback can't be loaded or submitted without a logged-in
  // account, since the FTH code / plan are pulled from the customer's own
  // subscription record. Checked synchronously on first render so the modal
  // doesn't flash in after the page has already painted.
  //
  // isTokenValid (not just "does a token string exist") so a stale/expired
  // token left over in localStorage doesn't skip the modal.
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(
    () => isTokenValid(localStorage.getItem("accessToken"))
  );

  const [ratings, setRatings] = useState<Record<string, number>>({});
  const [hoveredRating, setHoveredRating] = useState<Record<string, number>>({});
  const [nps, setNps] = useState<number | null>(null);
  const [hoveredNps, setHoveredNps] = useState<number | null>(null);
  const [selectedIssues, setSelectedIssues] = useState<string[]>([]);

  // FTH code and plan are no longer user-entered — they're pulled from the
  // customer's actual account/subscription data and shown read-only.
  const [fthCode, setFthCode] = useState("");
  const [selectedPlan, setSelectedPlan] = useState("");
  const [accountInfoLoading, setAccountInfoLoading] = useState(true);
  const [accountInfoError, setAccountInfoError] = useState<string | null>(null);

  const [comments, setComments] = useState("");
  const [showThankYouModal, setShowThankYouModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Keep this page's auth state in sync with logins/logouts that happen
  // elsewhere: another tab (storage event), or another component on this
  // same mount dispatching "ftthsa-auth-changed" (see apiService.login()).
  // Without this, logging in via some other flow while Feedback is mounted
  // wouldn't be picked up, and the modal would stay stuck open or closed
  // based on stale state.
  useEffect(() => {
    const syncAuth = () => {
      setIsLoggedIn(isTokenValid(localStorage.getItem("accessToken")));
    };
    window.addEventListener("ftthsa-auth-changed", syncAuth);
    window.addEventListener("storage", syncAuth);
    return () => {
      window.removeEventListener("ftthsa-auth-changed", syncAuth);
      window.removeEventListener("storage", syncAuth);
    };
  }, []);

  // Pull the customer's FTH code (account reference) and active plan name
  // from their subscription record — same source Profile / ManageSubscription use.
  // Gated on isLoggedIn so this only runs once we actually have a token —
  // either on initial mount (already logged in) or right after the
  // FeedbackLoginModal reports a successful login.
  useEffect(() => {
    if (!isLoggedIn) {
      return;
    }

    const loadAccountInfo = async () => {
      const portalEndCustomerId = localStorage.getItem("portalEndCustomerId");

      if (!portalEndCustomerId || !isTokenValid(localStorage.getItem("accessToken"))) {
        // Token/ID vanished or expired between mount and now — bounce back
        // to the login gate instead of showing a dead-end "not available".
        setIsLoggedIn(false);
        setAccountInfoLoading(false);
        return;
      }

      setAccountInfoLoading(true);
      try {
        const subscriptions = await fetchUserSubscriptions(portalEndCustomerId);

        if (subscriptions && subscriptions.length > 0) {
          const activeSubscription = subscriptions[0];
          setFthCode(activeSubscription.accountReference || "");
          setSelectedPlan(activeSubscription.portalProduct?.name || "Not available");
        } else {
          setAccountInfoError("No active subscription found on your account.");
        }
      } catch (err) {
        console.error("Failed to load account info for feedback form:", err);
        setAccountInfoError("Couldn't load your account details automatically.");
      } finally {
        setAccountInfoLoading(false);
      }
    };

    loadAccountInfo();
  }, [isLoggedIn]);

  const toggleIssue = (issue: string) => {
    setSelectedIssues((prev) =>
      prev.includes(issue) ? prev.filter((i) => i !== issue) : [...prev, issue]
    );
  };

  const overallRating =
    Object.values(ratings).length > 0
      ? Math.round((Object.values(ratings).reduce((a, b) => a + b, 0) / Object.values(ratings).length) * 10) / 10
      : null;

  const handleSubmit = async () => {
    // Belt-and-braces: the modal should already have blocked this, but if
    // the token expired mid-session, re-show the gate instead of letting a
    // 401 fall through to a generic error.
    if (!isTokenValid(localStorage.getItem("accessToken"))) {
      setIsLoggedIn(false);
      return;
    }

    setSubmitError(null);
    setIsSubmitting(true);

    const payload = {
      fth_code: fthCode || null,
      plan: selectedPlan || null,
      connection_speed: ratings.speed ?? null,
      reliability: ratings.reliability ?? null,
      support: ratings.support ?? null,
      value: ratings.value ?? null,
      installation: ratings.installation ?? null,
      likely_to_recommend: nps,
      issues_experienced: selectedIssues,
      comments: comments.trim() || null,
      status: "completed",
      submitted_at: new Date().toISOString(),
    };

    try {
      await apiClient.post("/surveys/", payload);
      setShowThankYouModal(true);
    } catch (err: any) {
      console.error("Feedback submission failed:", err);
      if (err?.response?.status === 401) {
        // Session expired / token invalid — send them back through the
        // login modal instead of just showing a dead-end error string.
        setSubmitError("Your session couldn't be verified. Please log in again.");
        setIsLoggedIn(false);
      } else {
        setSubmitError("Something went wrong submitting your feedback. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleModalOk = () => {
    setShowThankYouModal(false);
    navigate("/"); // change "/" if your landing page lives at a different route
  };

  return (
    <div className="fb-page">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800&family=DM+Sans:wght@300;400;500&display=swap');

        .fb-page {
          min-height: 100vh;
          background: #f7f7f7;
          font-family: 'DM Sans', sans-serif;
          padding: 0 0 80px 0;
        }

        .fb-hero {
          background:rgb(15, 23, 79);
          padding: 60px 24px 50px;
          text-align: center;
          position: relative;
          overflow: hidden;
        }

        .fb-hero::before {
          content: '';
          position: absolute;
          inset: 0;
          background: radial-gradient(ellipse at 60% 0%, hsla(19, 88%, 47%, 0.25) 0%, transparent 65%),
                      radial-gradient(ellipse at 20% 100%, hsla(19, 88%, 47%, 0.1) 0%, transparent 50%);
        }

        .fb-hero-inner {
          position: relative;
          z-index: 1;
          max-width: 620px;
          margin: 0 auto;
        }

        .fb-logo-tag {
          display: inline-block;
          background: #e2520f;
          color: #fff;
          font-family: 'Syne', sans-serif;
          font-weight: 700;
          font-size: 0.7rem;
          letter-spacing: 0.15em;
          text-transform: uppercase;
          padding: 5px 14px;
          border-radius: 4px;
          margin-bottom: 24px;
        }

        .fb-hero h1 {
          font-family: '', sans-serif;
          font-size: clamp(2rem, 5vw, 3.2rem);
          font-weight: 800;
          color: #fff;
          margin: 0 0 16px;
          line-height: 1.1;
        }

        .fb-hero h1 span {
          color: #e2520f;
        }

        .fb-hero p {
          color: rgba(255,255,255,0.6);
          font-size: 1rem;
          font-weight: 300;
          margin: 0;
          line-height: 1.6;
        }

        .fb-body {
          max-width: 720px;
          margin: 0 auto;
          padding: 0 20px;
        }

        .fb-section {
          background: #fff;
          border-radius: 16px;
          padding: 32px;
          margin-top: 24px;
          border: 1px solid #ebe9e3;
        }

        .fb-section-label {
          font-family: '', sans-serif; 
          font-weight: 700;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          color: #e2520f;
          margin-bottom: 6px;
        }

        .fb-section-title {
          font-family: '', sans-serif;
          font-size: 1.15rem;
          font-weight: 700;
          color: #111;
          margin: 0 0 24px;
        }

        /* Star ratings */
        .fb-category-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 14px 0;
          border-bottom: 1px solid #f0ede7;
          gap: 12px;
        }

        .fb-category-row:last-child {
          border-bottom: none;
          padding-bottom: 0;
        }

        .fb-category-row:first-child {
          padding-top: 0;
        }

        .fb-cat-info {
          display: flex;
          align-items: center;
          gap: 10px;
          min-width: 0;
        }

        .fb-cat-icon {
          font-size: 1.2rem;
          flex-shrink: 0;
        }

        .fb-cat-name {
          font-size: 0.95rem;
          font-weight: 500;
          color: #222;
          white-space: nowrap;
        }

        .fb-stars {
          display: flex;
          gap: 4px;
          flex-shrink: 0;
        }

        .fb-star {
          font-size: 1.5rem;
          cursor: pointer;
          transition: transform 0.15s ease, color 0.15s ease;
          color: #ddd;
          line-height: 1;
        }

        .fb-star.active {
          color: #e2520f;
        }

        .fb-star:hover {
          transform: scale(1.2);
        }

        /* NPS */
        .fb-nps-row {
          display: flex;
          gap: 6px;
          flex-wrap: wrap;
          margin-top: 8px;
        }

        .fb-nps-btn {
          width: 44px;
          height: 44px;
          border-radius: 10px;
          border: 1.5px solid #e0ddd7;
          background: #fff;
          font-family: '', sans-serif;
          font-weight: 600;
          font-size: 0.85rem;
          color: #555;
          cursor: pointer;
          transition: all 0.15s ease;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .fb-nps-btn:hover {
          border-color: #e2520f;
          color: #e2520f;
        }

        .fb-nps-btn.selected {
          background: #e2520f;
          border-color: #e2520f;
          color: #fff;
        }

        .fb-nps-labels {
          display: flex;
          justify-content: space-between;
          margin-top: 8px;
        }

        .fb-nps-labels span {
          font-size: 0.75rem;
          color: #999;
          font-weight: 300;
        }

        /* Read-only account-info display (FTH code / Plan) */
        .fb-readonly-display {
          width: 100%;
          padding: 12px 16px;
          border-radius: 10px;
          border: 1.5px solid #e0ddd7;
          font-family: 'DM Sans', sans-serif;
          font-size: 0.95rem;
          color: #222;
          background: #ffffff;
          box-sizing: border-box;
          display: flex;
          align-items: center;
          min-height: 46px;
        }

        .fb-readonly-display.loading {
          color: #aaa;
          font-weight: 300;
        }

        .fb-input-wrap .fb-readonly-display {
          padding-left: 44px;
        }

        .fb-account-error {
          margin-top: 8px;
          font-size: 0.78rem;
          color: #c0392b;
          font-weight: 400;
        }

        /* Issue chips */
        .fb-chips {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
          margin-top: 4px;
        }

        .fb-chip {
          padding: 8px 16px;
          border-radius: 50px;
          border: 1.5px solid #e0ddd7;
          font-size: 0.85rem;
          color: #555;
          cursor: pointer;
          transition: all 0.15s ease;
          background: #fff;
          font-family: 'DM Sans', sans-serif;
        }

        .fb-chip:hover {
          border-color: #e2520f;
          color: #e2520f;
        }

        .fb-chip.selected {
          background: #fff4f0;
          border-color: #e2520f;
          color: #e2520f;
          font-weight: 500;
        }

        /* Textarea */
        .fb-textarea {
          width: 100%;
          min-height: 110px;
          padding: 14px 16px;
          border-radius: 10px;
          border: 1.5px solid #e0ddd7;
          font-family: 'DM Sans', sans-serif;
          font-size: 0.95rem;
          color: #222;
          resize: vertical;
          outline: none;
          transition: border-color 0.15s;
          box-sizing: border-box;
          line-height: 1.6;
        }

        .fb-textarea:focus {
          border-color: #e2520f;
        }

        .fb-textarea::placeholder {
          color: #bbb;
          font-weight: 300;
        }

        /* Overall score pill */
        .fb-score-pill {
          display: flex;
          align-items: center;
          gap: 12px;
          background: #fff4f0;
          border: 1px solid #fcd5c3;
          border-radius: 12px;
          padding: 14px 20px;
          margin-top: 24px;
        }

        .fb-score-number {
          font-family: '', sans-serif;
          font-size: 2rem;
          font-weight: 800;
          color: #e2520f;
          line-height: 1;
        }

        .fb-score-text {
          font-size: 0.85rem;
          color: #555;
          font-weight: 300;
        }

        .fb-score-text strong {
          display: block;
          font-weight: 600;
          color: #222;
          font-size: 0.9rem;
        }

        /* FTH Code input wrap */
        .fb-input-wrap {
          position: relative;
        }

        .fb-input-icon {
          position: absolute;
          left: 14px;
          top: 50%;
          transform: translateY(-50%);
          font-size: 1rem;
          pointer-events: none;
        }

        .fb-input-hint {
          margin-top: 8px;
          font-size: 0.78rem;
          color: #aaa;
          font-weight: 300;
        }

        /* Submit */
        .fb-submit-wrap {
          margin-top: 32px;
          text-align: center;
        }

        .fb-submit-btn {
          background: #e2520f;
          color: #fff;
          border: none;
          padding: 16px 48px;
          border-radius: 12px;
          font-family: '', sans-serif;
          font-weight: 700;
          font-size: 1rem;
          letter-spacing: 0.05em;
          cursor: pointer;
          transition: background 0.2s, transform 0.15s;
        }

        .fb-submit-btn:hover {
          background: #c9470d;
          transform: translateY(-2px);
        }

        .fb-submit-btn:disabled {
          background: #e8b198;
          cursor: not-allowed;
          transform: none;
        }

        .fb-submit-note {
          margin-top: 12px;
          font-size: 0.8rem;
          color: #aaa;
          font-weight: 300;
        }

        .fb-submit-error {
          margin-top: 16px;
          font-size: 0.85rem;
          color: #c0392b;
          font-weight: 500;
        }

        /* Thank-you modal */
        .fb-modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(17, 17, 17, 0.55);
          backdrop-filter: blur(3px);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 24px;
          z-index: 1000;
        }

        .fb-modal {
          background: #fff;
          border-radius: 20px;
          padding: 40px 32px 32px;
          max-width: 380px;
          width: 100%;
          text-align: center;
          box-shadow: 0 24px 60px rgba(0,0,0,0.25);
        }

        .fb-modal-icon {
          width: 64px;
          height: 64px;
          border-radius: 50%;
          background: #e2520f;
          color: #fff;
          font-size: 1.8rem;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 20px;
        }

        .fb-modal h2 {
          font-family: '', sans-serif;
          font-size: 1.4rem;
          font-weight: 800;
          color: #111;
          margin: 0 0 10px;
        }

        .fb-modal p {
          color: #777;
          font-weight: 300;
          font-size: 0.9rem;
          margin: 0 0 28px;
          line-height: 1.5;
        }

        .fb-modal-ok-btn {
          background: #e2520f;
          color: #fff;
          border: none;
          padding: 13px 40px;
          border-radius: 12px;
          font-family: '', sans-serif;
          font-weight: 700;
          font-size: 0.95rem;
          letter-spacing: 0.04em;
          cursor: pointer;
          transition: background 0.2s, transform 0.15s;
        }

        .fb-modal-ok-btn:hover {
          background: #c9470d;
          transform: translateY(-2px);
        }

        @media (max-width: 520px) {
          .fb-section { padding: 24px 18px; }
          .fb-cat-name { font-size: 0.85rem; }
          .fb-nps-btn { width: 36px; height: 36px; font-size: 0.78rem; }
        }
      `}</style>

      {/* Hero */}
      <div className="fb-hero">
        <motion.div
          className="fb-hero-inner"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55 }}
        >
          {/* <div className="fb-logo-tag">FTTHSA</div> */}
          <h1>How's your <span>fibre</span> experience?</h1>
          <p>Takes less than 2 minutes. Your honest feedback shapes our service.</p>
        </motion.div>
      </div>

      <div className="fb-body">

        {/* FTH Code - pulled from account, read-only */}
        <motion.div
          className="fb-section"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <div className="fb-section-label">Step 1</div>
          <div className="fb-section-title">Your Account Code</div>
          <div className="fb-input-wrap">
            <span className="fb-input-icon">🪪</span>
            <div className={`fb-readonly-display ${accountInfoLoading ? "loading" : ""}`}>
              {accountInfoLoading ? "Loading your account details..." : (fthCode || "Not available")}
            </div>
          </div>
          <p className="fb-input-hint">Your Account Code is been used to provide your experience in your account</p>
          {accountInfoError && <p className="fb-account-error">{accountInfoError}</p>}
        </motion.div>

        {/* Plan - pulled from active subscription, read-only */}
        <motion.div
          className="fb-section"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
        >
          <div className="fb-section-label">Step 2</div>
          <div className="fb-section-title">Your Active Plan</div>
          <div className={`fb-readonly-display ${accountInfoLoading ? "loading" : ""}`}>
            {accountInfoLoading ? "Loading your plan..." : (selectedPlan || "Not available")}
          </div>
        </motion.div>

        {/* Star ratings */}
        <motion.div
          className="fb-section"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <div className="fb-section-label">Step 3</div>
          <div className="fb-section-title">Rate your experience</div>

          {categories.map((cat) => {
            const activeVal = hoveredRating[cat.id] ?? ratings[cat.id] ?? 0;
            return (
              <div className="fb-category-row" key={cat.id}>
                <div className="fb-cat-info">
                  <span className="fb-cat-icon">{cat.icon}</span>
                  <span className="fb-cat-name">{cat.label}</span>
                </div>
                <div className="fb-stars">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <span
                      key={star}
                      className={`fb-star ${activeVal >= star ? "active" : ""}`}
                      onMouseEnter={() => setHoveredRating((h) => ({ ...h, [cat.id]: star }))}
                      onMouseLeave={() => setHoveredRating((h) => { const n = { ...h }; delete n[cat.id]; return n; })}
                      onClick={() => setRatings((r) => ({ ...r, [cat.id]: star }))}
                    >
                      ★
                    </span>
                  ))}
                </div>
              </div>
            );
          })}

          {overallRating && (
            <div className="fb-score-pill">
              <div className="fb-score-number">{overallRating}</div>
              <div className="fb-score-text">
                <strong>Overall score</strong>
                based on {Object.keys(ratings).length} of {categories.length} categories rated
              </div>
            </div>
          )}
        </motion.div>

        {/* NPS */}
        <motion.div
          className="fb-section"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <div className="fb-section-label">Step 4</div>
          <div className="fb-section-title">How likely are you to recommend FTTHSA?</div>
          <div className="fb-nps-row">
            {Array.from({ length: 11 }, (_, i) => (
              <button
                key={i}
                className={`fb-nps-btn ${nps === i ? "selected" : ""}`}
                onMouseEnter={() => setHoveredNps(i)}
                onMouseLeave={() => setHoveredNps(null)}
                onClick={() => setNps(i)}
              >
                {i}
              </button>
            ))}
          </div>
          <div className="fb-nps-labels">
            <span>Not at all likely</span>
            <span>Extremely likely</span>
          </div>
        </motion.div>

        {/* Issues */}
        <motion.div
          className="fb-section"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <div className="fb-section-label">Step 5</div>
          <div className="fb-section-title">Any issues you've experienced?</div>
          <div className="fb-chips">
            {issueOptions.map((issue) => (
              <button
                key={issue}
                className={`fb-chip ${selectedIssues.includes(issue) ? "selected" : ""}`}
                onClick={() => toggleIssue(issue)}
              >
                {issue}
              </button>
            ))}
          </div>
        </motion.div>

        {/* Comments */}
        <motion.div
          className="fb-section"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <div className="fb-section-label">Step 6 — Optional</div>
          <div className="fb-section-title">Anything else you'd like to share?</div>
          <textarea
            className="fb-textarea"
            placeholder="Tell us what we're doing well, or where we can improve..."
            value={comments}
            onChange={(e) => setComments(e.target.value)}
          />
        </motion.div>

        {/* Submit */}
        <motion.div
          className="fb-submit-wrap"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
        >
          <button
            className="fb-submit-btn"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? "Submitting..." : "Submit Feedback"}
          </button>
          <p className="fb-submit-note">Takes under 2 minutes</p>
          {submitError && <p className="fb-submit-error">{submitError}</p>}
        </motion.div>

      </div>

      {/* Thank-you modal */}
      <AnimatePresence>
        {showThankYouModal && (
          <motion.div
            className="fb-modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleModalOk}
          >
            <motion.div
              className="fb-modal"
              initial={{ scale: 0.9, opacity: 0, y: 10 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ type: "spring", stiffness: 260, damping: 22 }}
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-labelledby="fb-modal-title"
            >
              <div className="fb-modal-icon">✓</div>
              <h6 id="fb-modal-title">Thank you for submitting feedback</h6>
              <p>Your response helps us improve our fibre service for everyone.</p>
              <button className="fb-modal-ok-btn" onClick={handleModalOk}>
                OK
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Auth gate — blocks the page until the user logs in */}
      <FeedbackLoginModal
        isOpen={!isLoggedIn}
        onLoginSuccess={() => setIsLoggedIn(true)}
      />
    </div>
  );
}